import { getJangjiRecommendRequests, patchJangjiRecommendRequest } from "@/apis/jangjiRecommend";
import { HackleExperimentContainer } from "@/components/HackleExperimentContainer";
import AuthProvider from "@/components/Provider/AuthProvider";
import { TimeSplitContainer } from "@/components/TimeSplitContainer";
import { useDialog } from "@/hooks/useDialog";
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
} from "@chakra-ui/react";
import {
  Button,
  call,
  ColorIcon,
  colors,
  customEvent,
  FUNERAL_SIDO,
  FUNERAL_SIGUNGU,
  HStack,
  queryKeys,
  Spacing,
  VStack,
} from "goi_common";
import { useRouter } from "next/router";
import { ReactNode, useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { JangjiConsultTimeSelect } from "./components/MatchFacility/JangjiConsultTimeSelect";
import {
  BACKEND_MATCH_KEYS,
  JangjiRelaxedSurvey,
} from "./components/MatchFacility/JangjiRelaxedSurvey";
import { JangjiResultStepGuide } from "./components/MatchFacility/JangjiResultStepGuide";
import { 장지유형_LIST, 장지희망서비스_LIST } from "./constants/request";
import { JangjiResultCell, resolveJangjiResultCell } from "@/utils/jangjiCell";
import { useHideCallCta } from "@/hooks/useHideCallCta";

/**
 * Hackle #336 - 장지 견적 공설/사설 가치제안 분기.
 *
 * 로그인 화면(`pages/login/index.tsx` 의 JangjiQuoteLoginScreen)과 **같은 키를 공유한다.**
 * 하나의 실험이 두 표면에 걸린다.
 *
 *   로그인 완료율   Phase 1(로그인 화면) 단독 효과. 결과 페이지는 로그인 이후라 영향 없음
 *   goi_call, 유효리드  두 표면 합산 효과
 *
 * 결과 페이지 단독으로는 판정이 안 된다(주 110명. 2셀로 쪼개면 arm당 22명). 로그인 완료율로
 * 실험이 판정되고 이 화면의 처치가 같이 실려 간다.
 *
 * 여기서 useCustomVariation 을 다시 호출하는 이유: 로그인 화면 할당을 sessionStorage 로
 * 나르면 결과 페이지 재방문에서 처치를 놓친다(GA4 기준 유저당 4회 열린다). Hackle 은 같은
 * 식별자에 같은 처치를 주므로 재호출이 안전하고, 모수 오염은 아래 `cell` 게이트가 막는다.
 *
 * 직전까지 이 자리에 있던 #324(O/X 퀴즈)는 A안 승리로 종료돼 컨테이너를 걷어냈다.
 * #333 이 288 종료 후 같은 처리를 한 전례가 있다.
 */
const JANGJI_EXPERIMENT_KEY = 336;

/** 시도/시군구 코드 → "시도 시군구" 문자열. 못 풀면 빈 문자열(undefined 렌더 방지) */
function buildRegion(sido?: string | number, sigungu?: string | number): string {
  const sidoName = FUNERAL_SIDO.get(Number(sido));
  if (!sidoName) return "";
  const sigunguName = FUNERAL_SIGUNGU.get(Number(sido))?.get(Number(sigungu));
  return `${sidoName} ${sigunguName ?? ""}`.trim();
}

/** 개별 장지 견적은 시설명을 memo에 이 접두어와 함께 담아 보낸다(별도 필드가 없다) */
const INDIVIDUAL_QUOTE_MEMO_PREFIX = "[개별 장지 견적 제출]: ";

/**
 * 여유 설문 답변이 memo 뒤에 이 마커와 함께 붙는다. 견적 memo 는 `partition("|")` 로
 * 잘라 쓰지만 장지는 접두어 + slice 라, 마커 앞부분만 취하지 않으면 시설명에 답변이
 * 딸려온다. 계약은 wiki/plans/jangji-relaxed-lead-triage.md 3-4.
 *
 * 마커가 없는 memo(설문 도입 이전 데이터)에서는 split 이 원본 하나짜리 배열을 주므로
 * 동작이 변하지 않는다. 그래서 설문 UI 보다 먼저 배포해도 안전하다.
 *
 * 구분자(앞 공백, 뒤 콜론)를 뺀 이유: 추천 견적은 memo 가 빈 문자열이라 답변을 붙이면
 * 앞 공백 유무가 갈린다. 재노출 방지 판정(`memo.includes`)도 같은 문자열을 써야 한다.
 */
const SURVEY_ANSWER_MARKER = "여유설문응답완료";

function stripSurveyAnswer(memo: string): string {
  return memo.split(SURVEY_ANSWER_MARKER)[0];
}

/**
 * memo 에서 여유 설문 답변 원문만 꺼낸다. 서버 `extract_jangji_survey_answer` 와 같은 방식이다
 * (`{마커}: {답변}` 에서 마커와 앞 구분자를 떼어낸다).
 *
 * 마커 뒷부분만 취한다. 개별 장지 견적은 memo 앞에 시설명이 붙어 있어서
 * (`[개별 장지 견적 제출]: {시설명}`) 전체를 보면 시설명이 섞인다.
 */
function extractSurveyAnswer(memo?: string): string | undefined {
  if (!memo?.includes(SURVEY_ANSWER_MARKER)) return undefined;
  const answer = memo
    .split(SURVEY_ANSWER_MARKER)[1]
    ?.replace(/^[:\s]+/, "")
    .trim();
  return answer || undefined;
}

/**
 * 설문 답변이 상담행인지. 백엔드와 같은 키(`BACKEND_MATCH_KEYS`)를 본다.
 *
 * 무효행(`장지 계약만`, `그냥 둘러보는 중`)에는 상담시간을 받지 않는다. 자동 무효로 갈
 * 고객에게 콜백을 약속하면 안 된다.
 */
function isConsultSurveyAnswer(memo?: string): boolean {
  const answer = extractSurveyAnswer(memo);
  if (!answer) return false;
  return BACKEND_MATCH_KEYS.some((key) => answer.includes(key));
}

/**
 * 여유 설문 대상. `장지준비긴급도_LIST` 의 value 이자 서버 `JangjiStatusType` 의 값이다.
 * `이장` 은 제외한다. 유효율 0.6% 이고 4지선다가 이장 맥락에 맞지 않아 전원이 무효 분기로
 * 간다. 근거는 wiki/plans/jangji-relaxed-lead-triage.md 3-1.
 */
const RELAXED_JANGJI_STATUS = "탐색";

/**
 * 개별 장지 견적으로 들어온 경우의 시설명. 추천 견적은 접두어가 없어 undefined를 준다.
 * 제출부가 `${facility?.companyname}`로 만들어서 문자열 "undefined"가 들어올 수 있다.
 */
function extractFacilityName(memo?: string): string | undefined {
  if (!memo?.startsWith(INDIVIDUAL_QUOTE_MEMO_PREFIX)) return undefined;
  const name = stripSurveyAnswer(memo.slice(INDIVIDUAL_QUOTE_MEMO_PREFIX.length)).trim();
  return name && name !== "undefined" ? name : undefined;
}

interface Answers {
  desiredType?: string;
  factor?: string;
  addtionalService?: string;
  origin: string;
  jangji: string;
  memo: string;
}

export default function MatchRecommendationFacilityContainer({
  publicFacilityNames = [],
}: {
  /** getStaticProps 가 내려주는 공설 시설명. 기본값은 전원 사설 판정이 아니라 셀 없음(실험 밖) */
  publicFacilityNames?: string[];
}) {
  const router = useRouter();
  const { onOpen, onClose } = useDialog();
  const hasPrevious = router.query.hasPrevious as string;

  useEffect(() => {
    if (hasPrevious) {
      onOpen({
        type: 1,
        closeOnOverlayClick: false,
        header: "이미 제출한 장지 견적서가 있어요",
        children: (
          <>
            작성해주신 견적서를 바탕으로 <br /> 상담이 진행중인 상태에요.
            <br /> 견적 내용을 수정하시려면,
            <br /> 카카오톡 채널로 문의주세요.
          </>
        ),
        footerProps: {
          text: "닫기",
          onClick: () => {
            customEvent({
              newGtm: {
                path: "facility_match_recommendation",
                trigger_and_target: "check_previous_result-uv_consult-list",
              },
            });
            onClose();
          },
        },
      });
    }
  }, [hasPrevious]);

  const { data, isLoading } = useQuery(
    queryKeys.jangjiRecommendRequest,
    getJangjiRecommendRequests,
    {
      select: (result) => {
        if (result[0]) {
          return result[0];
        } else return undefined;
      },
    },
  );

  const answers: Answers | undefined = useMemo(() => {
    if (data)
      return {
        desiredType: 장지유형_LIST?.find((item) => item.value === data.desired_type)?.text,
        factor: data.most_important_factor,
        addtionalService: 장지희망서비스_LIST?.find(
          (item) => item.value === data.additional_service,
        )?.text,
        origin: buildRegion(data.origin_sido, data.origin_sigungu),
        jangji: buildRegion(data.jangji_sido, data.jangji_sigungu),
        memo: data.memo,
      };
  }, [data]);

  const queryClient = useQueryClient();
  const [surveyAnswer, setSurveyAnswer] = useState("");

  /**
   * 여유 설문 노출 조건. 결과 화면은 유저당 여러 번 열리므로(#336 주석 기준 GA4 로 4회)
   * 답변이 memo 에 남으면 다시 띄우지 않는다. 판정 문자열은 파서와 같은 상수를 쓴다.
   */
  const showSurvey =
    data?.status === RELAXED_JANGJI_STATUS && !(data?.memo ?? "").includes(SURVEY_ANSWER_MARKER);

  const { mutate: submitSurvey, isLoading: isSubmittingSurvey } = useMutation(
    async () => {
      if (!data?.id) {
        throw new Error("장지 추천 요청서 id 가 없어 설문 답변을 저장할 수 없다");
      }
      // 기존 memo 와 합치고 마커를 붙이는 것은 서버 몫이다. 원문만 보낸다
      return patchJangjiRecommendRequest(data.id, { memo: surveyAnswer });
    },
    {
      onSuccess: () => queryClient.invalidateQueries(queryKeys.jangjiRecommendRequest),
    },
  );

  const handleSurveySubmit = () => {
    customEvent({
      newGtm: {
        path: "jangji_done",
        trigger_and_target: "srvy_submit",
        text: surveyAnswer,
      },
      triggerHackle: true,
    });
    submitSurvey();
  };

  /**
   * 상담시간 블록. 결과 화면 맨 아래에 붙는다.
   *
   * 설문을 먼저 받고, 답변이 상담행일 때만 시간을 묻는다. 견적은 야간에 설문을 스킵하고
   * 상담시간만 받지만(`nightBody`), 장지 설문은 상담 필요도 분류가 목적이라 야간 유입분이
   * 분류에서 빠지면 커버리지에 구멍이 난다. 그래서 순서를 설문 -> 상담시간으로 둔다.
   *
   * 시간대 판정은 `TimeSplitContainer` 에 맡긴다. 화면 전체를 가르지 않고 이 블록의 노출만
   * 가른다. 주간에는 바로 콜백하면 되므로 물어볼 필요가 없다. 판정 전(`isLoading`)에는
   * 컨테이너가 아무것도 렌더하지 않아 블록만 안 보인다.
   *
   * 기획은 wiki/plans/jangji-daybreak-consult-time.md 3-2.
   */
  const consultTimeBlock: ReactNode =
    data?.id && data?.status === RELAXED_JANGJI_STATUS && isConsultSurveyAnswer(data?.memo) ? (
      <TimeSplitContainer
        night={
          <JangjiConsultTimeSelect
            requestId={data.id}
            surveyAnswer={extractSurveyAnswer(data?.memo)}
          />
        }
        daytime={<></>}
      />
    ) : null;

  /**
   * 실험 모수 게이트.
   *
   * 시설명이 없으면 null 이고 그때는 실험 컨테이너를 아예 마운트하지 않는다. 추천 견적
   * 제출자(시설 미선택)와 화장장 페이지 유입자가 여기서 걸러진다. 마운트하면 그 사람들까지
   * Hackle 노출로 기록돼 로그인 화면과 모수가 어긋난다.
   */
  const cell: JangjiResultCell | null = useMemo(() => {
    if (publicFacilityNames.length === 0) return null;
    return resolveJangjiResultCell(extractFacilityName(data?.memo), new Set(publicFacilityNames));
  }, [data?.memo, publicFacilityNames]);

  if (isLoading) {
    return null;
  }

  return (
    <AuthProvider redirectToIfNotLoggedIn="/match/request/">
      {showSurvey ? (
        <JangjiRelaxedSurvey
          answer={surveyAnswer}
          onAnswerChange={setSurveyAnswer}
          onSubmit={handleSurveySubmit}
          isSubmitting={isSubmittingSurvey}
        />
      ) : cell ? (
        <HackleExperimentContainer
          experimentKey={JANGJI_EXPERIMENT_KEY}
          variants={{
            A: <ResultLayout answers={answers} consultTimeBlock={consultTimeBlock} />,
            B: (
              <VStack alignItems="center">
                <JangjiResultStepGuide
                  cell={cell}
                  facilityName={extractFacilityName(data?.memo)}
                  regionLabel={answers?.jangji}
                />
              </VStack>
            ),
          }}
          contextExtra={{ jangji_cell: cell }}
        />
      ) : (
        <ResultLayout answers={answers} consultTimeBlock={consultTimeBlock} />
      )}
    </AuthProvider>
  );
}

/**
 * A안 = 현행 결과 화면.
 *
 * #336 은 2026-08-28 A안 승리로 종료됐다. 그래서 상담시간 블록을 이 트리에만 넣는다.
 * B안(`JangjiResultStepGuide`)에는 넣지 않는다. baseline 을 지켜야 했던 실험 기간에는
 * 이 트리를 한 픽셀도 건드리지 않았다.
 */
function ResultLayout({
  answers,
  consultTimeBlock,
}: {
  answers?: Answers;
  /** 야간, 주말 + 상담행 답변일 때만 들어온다. 그 외에는 null */
  consultTimeBlock?: ReactNode;
}) {
  const router = useRouter();
  const consultCallBlock = <ConsultCallBlock jangji={answers?.jangji} />;

  return (
    <VStack alignItems="center">
      <VStack id="wrapper" width="100%" maxWidth="480px">
        <VStack>
          <Box padding="20px 24px 12px" className="head2_b">
            장지 추천(맞춤형)
          </Box>
          <Box padding="0 16px">
            <HStack
              as="button"
              onClick={() => {
                customEvent({
                  newGtm: {
                    path: "facility_match_recommendation",
                    trigger_and_target: "recommend-uv_match_request",
                  },
                });
                router.push("/match/request/");
              }}
              width="100%"
              borderRadius="5px"
              background={colors.orange50}
              padding="16px 20px 16px 10px"
              alignItems="center"
              justifyContent="space-between"
            >
              <div className="caption_12_b orange650 font-left">
                상조 서비스, 장례식장도 추천해드려요
                <br />
                <span className="caption_12_b gray500">
                  고이에서 까다롭게 선별한 장례지도사 추천받기
                </span>
              </div>
              <ColorIcon name="arrow" color={colors.gray500} width="16px" height="16px" />
            </HStack>
          </Box>
          <Spacing gap={8} />

          <Accordion allowToggle allowMultiple defaultIndex={[0]}>
            <AccordionItem>
              <AccordionButton
                display="flex"
                justifyContent="space-between"
                padding="16px 24px"
                _expanded={{
                  background: "white",
                }}
                _hover={{
                  background: "white",
                }}
              >
                <Box
                  as="h3"
                  className="subtitle_14_b gray900"
                  paddingRight="24px"
                  wordBreak="keep-all"
                  textAlign="left"
                >
                  장지 견적서
                  <br />
                  <span className="caption_12_b gray500">고객님이 남겨주신 답변이에요</span>
                </Box>
                <AccordionIcon width="24px" height="24px" color={colors.gray300} />
              </AccordionButton>
              {answers && (
                <AccordionPanel padding="0">
                  <Box
                    as="p"
                    padding="24px"
                    background={colors.warmGray50}
                    className="body_14_r gray700"
                  >
                    <table style={{ width: "100%" }}>
                      <VStack as="tbody" gap="10px" alignItems="start">
                        <TableRow
                          title="희망 유형"
                          description={
                            answers.desiredType
                              ? answers.desiredType
                              : stripSurveyAnswer(answers.memo).replace(
                                  INDIVIDUAL_QUOTE_MEMO_PREFIX,
                                  "",
                                )
                          }
                        />
                        {answers.factor && answers.factor !== "모르겠어요" && (
                          <TableRow title="선호하는 요인" description={answers.factor} />
                        )}
                        {answers.addtionalService && answers.addtionalService !== "모르겠어요" && (
                          <TableRow title="희망 서비스" description={answers.addtionalService} />
                        )}
                        {answers.origin && (
                          <TableRow title="지역(현재)" description={answers.origin} />
                        )}
                        {answers.jangji && (
                          <TableRow title="장지 희망지역" description={answers.jangji} />
                        )}
                      </VStack>
                    </table>
                  </Box>
                </AccordionPanel>
              )}
            </AccordionItem>
          </Accordion>
          {consultCallBlock}
          {consultTimeBlock}
        </VStack>
      </VStack>
    </VStack>
  );
}

/**
 * 장지 상담 전화 블록. 렌더 내용은 기존 그대로이고 위치만 B안에서 위로 올라간다.
 *
 * 이벤트는 `quickcall` 을 유지한다. 컴포넌트가 이동해도 기존 이름을 쓰는 것이 프로젝트
 * 규칙이고(데이터 연속성), 이 실험의 1차 판정에 goi_call 이 들어가서 더더욱 끊으면 안 된다.
 */
function ConsultCallBlock({ jangji }: { jangji?: string }) {
  // 이 화면은 콜 유입이 크지만(goi_call 발화율 37%) 전화 리드가 성과로 이어지지 않아
  // "지금 바로 문의하기" 를 숨긴다. 안내 문구는 아웃바운드 예고라 그대로 둔다.
  // 롤백은 useHideCallCta 참고. 관련 실험 #336, #324 는 종료됨.
  const hideCallCta = useHideCallCta();

  return (
    <VStack padding="40px">
      <Box className="subtitle_18_b font-center">
        <span className="orange650">{jangji}</span> 장지는
        <br />
        전문 상담이 필요해요
      </Box>
      <Spacing gap={16} />
      <Box className="body_14_r font-center">
        현재 {jangji} 장지는
        <br />
        직접 전문 상담을 진행해드리고 있어요.
        <br />
        <br />
        작성해주신 응답을 바탕으로
        <br />
        전화를 드릴 예정이에요.
      </Box>
      {hideCallCta || (
        <>
          <Spacing gap={24} />

          <Button
            className="button_14_sb white"
            css={{
              margin: "auto",
              width: "fit-content",
              backgroundColor: colors.orange650,
              padding: "10px 12px",
              borderRadius: "5px",
            }}
            onClick={() => {
              customEvent({
                newGtm: {
                  path: "facility_match_recommendation",
                  trigger_and_target: "quickcall",
                },
              });
              call("1666-9784");
            }}
          >
            지금 바로 문의하기
          </Button>
        </>
      )}
    </VStack>
  );
}

const TableRow = ({ title, description }: { title: string; description?: string }) => {
  return (
    <HStack as="tr">
      <Box as="td" className="subtitle_14_sb gray600" minWidth="110px">
        {title}
      </Box>
      <Box as="td" className="body_14_m gray900">
        {description}
      </Box>
    </HStack>
  );
};
